import { HourFormat } from '@mews/enums';
import { mod, padStartZero } from '../func';
import { HOURS_IN_DAY, MINUTES_IN_HOUR, SECONDS_IN_MINUTE } from './constants';
import { StructuredTime, TimeLocalization } from './types';

export interface TimeService<T = StructuredTime> {
    getDefaultTime: () => T;
    getHours: (time: T) => number;
    getMinutes: (time: T) => number;
    getSeconds: (time: T) => number;
    addHours: (time: T, additionalHours: number) => T;
    addMinutes: (time: T, additionalMinutes: number) => T;
    addSeconds: (time: T, additionalSeconds: number) => T;
    getTimeUniqueName: (time: T) => string;
    getTimeFromHoursAndMinutes: (hours: number, minutes: number) => T;
    has24HourFormat: () => boolean;
    fromIsoString: (isoString: string) => T;
    isValidShortString: (timeString: string) => boolean;
    isValidString: (timeString: string) => boolean;
    fromShortString: (timeString: string) => T;
    fromString: (timeString: string) => T;
    getShortString: (time: T) => string;
    isTimeEqual: (tme1: T, time2: T) => boolean;
    isTimeLess: (tme1: T, time2: T) => boolean;
    isTimeGreater: (tme1: T, time2: T) => boolean;
    isTimeLessOrEqual: (tme1: T, time2: T) => boolean;
    isTimeGreaterOrEqual: (tme1: T, time2: T) => boolean;
    getIsoString: (time: T) => string;
    getString: (time: T) => string;
    getMeridiemLabels: () => string[];
    getMeridiemPosition: () => number;
    shouldPadHours: () => boolean;
    fromValueString: (value: string) => T;
}

export interface TimeServiceFactory<T = StructuredTime> {
    getLocalization: () => TimeLocalization;
    getIsoString: (time: T) => string;
    getString: (time: T) => string;
    getShortString: (time: T) => string;
    isValidString: (timeString: string) => boolean;
    isValidShortString: (timeString: string) => boolean;
    fromString: (timeString: string) => T;
    fromShortString: (timeString: string) => T;
    fromIsoString: (isoString: string) => T;
}

export default ({
    getLocalization,
    getIsoString,
    getString,
    getShortString,
    isValidString,
    isValidShortString,
    fromIsoString,
    fromString,
    fromShortString,
}: TimeServiceFactory): TimeService => {
    const addHours = (time: StructuredTime, additionalHours: number): StructuredTime => ({
        ...time,
        hours: mod(time.hours + additionalHours, HOURS_IN_DAY),
    });

    const addMinutes = (time: StructuredTime, additionalMinutes: number): StructuredTime => {
        const totalMinutes = time.minutes + additionalMinutes;
        const newMinutes = mod(totalMinutes, MINUTES_IN_HOUR);

        const hourChange = Math.floor(totalMinutes / HOURS_IN_DAY);
        const { hours } = hourChange !== 0 ? addHours(time, hourChange) : time;

        return {
            ...time,
            minutes: newMinutes,
            hours,
        };
    };

    const addSeconds = (time: StructuredTime, additionalSeconds: number): StructuredTime => {
        const totalSeconds = time.seconds + additionalSeconds;
        const newSeconds = mod(totalSeconds, SECONDS_IN_MINUTE);

        const minutesChange = Math.floor(totalSeconds / SECONDS_IN_MINUTE);
        const { hours, minutes } = minutesChange !== 0 ? addMinutes(time, minutesChange) : time;

        return {
            hours,
            minutes,
            seconds: newSeconds,
        };
    };

    const isTimeEqual = (
        { hours, minutes, seconds }: StructuredTime,
        { hours: hours2, minutes: minutes2, seconds: seconds2 }: StructuredTime
    ): boolean => hours === hours2 && minutes === minutes2 && seconds === seconds2;

    const isTimeLess = (
        { hours, minutes, seconds }: StructuredTime,
        { hours: hours2, minutes: minutes2, seconds: seconds2 }: StructuredTime
    ): boolean =>
        hours < hours2 ||
        (hours === hours2 && minutes < minutes2) ||
        (hours === hours2 && minutes === minutes2 && seconds < seconds2);
    const isTimeGreater = (
        { hours, minutes, seconds }: StructuredTime,
        { hours: hours2, minutes: minutes2, seconds: seconds2 }: StructuredTime
    ): boolean =>
        hours > hours2 ||
        (hours === hours2 && minutes > minutes2) ||
        (hours === hours2 && minutes === minutes2 && seconds > seconds2);

    const has24HourFormat = (): boolean => getLocalization().hourFormat === HourFormat.TwentyFour;

    return {
        getDefaultTime: (): StructuredTime => ({
            hours: 0,
            minutes: 0,
            seconds: 0,
        }),

        getHours: ({ hours }: StructuredTime): number => hours,
        getMinutes: ({ minutes }: StructuredTime): number => minutes,
        getSeconds: ({ seconds }: StructuredTime): number => seconds,

        addHours,
        addMinutes,
        addSeconds,

        getTimeUniqueName: ({ hours, minutes, seconds }: StructuredTime): string =>
            `${padStartZero(hours)}:${padStartZero(minutes)}:${padStartZero(seconds)}`,

        isTimeEqual,
        isTimeLess,
        isTimeGreater,
        isTimeLessOrEqual: (time1: StructuredTime, time2: StructuredTime): boolean => !isTimeGreater(time1, time2),
        isTimeGreaterOrEqual: (time1: StructuredTime, time2: StructuredTime): boolean => !isTimeLess(time1, time2),

        getIsoString,
        getString,
        getShortString,

        isValidString,
        isValidShortString,
        has24HourFormat,

        fromIsoString,
        fromString,
        fromShortString,

        getMeridiemLabels: (): string[] => {
            const { anteMeridiem, postMeridiem } = getLocalization();
            return [anteMeridiem, postMeridiem];
        },

        getMeridiemPosition: () => {
            const { cultureCode } = getLocalization();
            /**
             * This knowledge is based on manual analysis of time string formats of all cultures used on backend.
             */
            return ['zh-TW', 'zh-SG', 'ko-KR'].includes(cultureCode) ? 0 : 1;
        },

        shouldPadHours: (): boolean => {
            const { timeFormat } = getLocalization();
            const neededFormat = has24HourFormat() ? 'HH' : 'hh';

            return timeFormat.includes(neededFormat);
        },

        getTimeFromHoursAndMinutes: (hours = 0, minutes = 0): StructuredTime => ({
            hours,
            minutes,
            seconds: 0,
        }),

        fromValueString(value) {
            const [hours, minutes, seconds] = value.split(':');

            return {
                hours: Number(hours),
                minutes: Number(minutes),
                seconds: Number(seconds),
            };
        },
    };
};
